import { type FC, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
	MetaAdsAttributionWindow,
	MetaAdsBidStrategy,
	MetaAdsBudgetType,
	MetaAdsCampaignObjective,
	MetaAdsConversionEvent,
	MetaAdsConversionLocation,
	MetaAdsOptimizationGoal,
} from "shared";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { AudienceTargetingSection } from "./audiences/AudienceTargetingSection";
import { InterestTargetingSection } from "./audiences/InterestTargetingSection";
import { useCampaignWizard } from "./CampaignWizardContext";
import { PixelSelectField } from "./components/PixelSelectField";
import { GeoTargetingSection } from "./geo/GeoTargetingSection";
import { centsToDollars, dollarsToCents } from "./metaAdsMoney";

function getOptimizationGoalsForObjective(
	objective: MetaAdsCampaignObjective,
): Array<{ value: MetaAdsOptimizationGoal; label: string }> {
	switch (objective) {
		case MetaAdsCampaignObjective.OUTCOME_AWARENESS:
			return [
				{ value: MetaAdsOptimizationGoal.REACH, label: "Reach" },
				{
					value: MetaAdsOptimizationGoal.AD_RECALL_LIFT,
					label: "Ad Recall Lift",
				},
			];
		case MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT:
			return [
				{
					value: MetaAdsOptimizationGoal.POST_ENGAGEMENT,
					label: "Post Engagement",
				},
				{
					value: MetaAdsOptimizationGoal.THRUPLAY,
					label: "ThruPlay (Video Views)",
				},
				{
					value: MetaAdsOptimizationGoal.CONVERSATIONS,
					label: "Conversations",
				},
				{ value: MetaAdsOptimizationGoal.PAGE_LIKES, label: "Page Likes" },
			];
		case MetaAdsCampaignObjective.OUTCOME_SALES:
			return [
				{
					value: MetaAdsOptimizationGoal.OFFSITE_CONVERSIONS,
					label: "Conversions",
				},
				{ value: MetaAdsOptimizationGoal.VALUE, label: "Value (ROAS)" },
				{ value: MetaAdsOptimizationGoal.LINK_CLICKS, label: "Link Clicks" },
			];
		case MetaAdsCampaignObjective.OUTCOME_LEADS:
			return [
				{
					value: MetaAdsOptimizationGoal.LEAD_GENERATION,
					label: "Lead Generation",
				},
				{ value: MetaAdsOptimizationGoal.LINK_CLICKS, label: "Link Clicks" },
				{
					value: MetaAdsOptimizationGoal.LANDING_PAGE_VIEWS,
					label: "Landing Page Views",
				},
			];
		default:
			return [
				{ value: MetaAdsOptimizationGoal.LINK_CLICKS, label: "Link Clicks" },
				{
					value: MetaAdsOptimizationGoal.LANDING_PAGE_VIEWS,
					label: "Landing Page Views",
				},
			];
	}
}

const PLACEMENT_OPTIONS = [
	{ id: "facebook_feed", label: "Facebook Feed" },
	{ id: "instagram_feed", label: "Instagram Feed" },
	{ id: "instagram_stories", label: "Instagram Stories" },
	{ id: "instagram_reels", label: "Instagram Reels" },
	{ id: "facebook_stories", label: "Facebook Stories" },
	{ id: "facebook_reels", label: "Facebook Reels" },
	{ id: "audience_network", label: "Audience Network" },
];

export const StepAdSet: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useCampaignWizard();
	const [error, setError] = useState("");
	const [placementMode, setPlacementMode] = useState<"automatic" | "manual">(
		draft.placements === "automatic" ? "automatic" : "manual",
	);

	const isAwareness =
		draft.objective === MetaAdsCampaignObjective.OUTCOME_AWARENESS;
	const isReachOptimization =
		isAwareness && draft.optimizationGoal === MetaAdsOptimizationGoal.REACH;
	const isEngagement =
		draft.objective === MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT;
	const isLeads = draft.objective === MetaAdsCampaignObjective.OUTCOME_LEADS;
	const isSales = draft.objective === MetaAdsCampaignObjective.OUTCOME_SALES;

	const manualPlacements = Array.isArray(draft.placements)
		? draft.placements
		: [];

	const handlePlacementToggle = (id: string) => {
		const updated = manualPlacements.includes(id)
			? manualPlacements.filter((p) => p !== id)
			: [...manualPlacements, id];
		updateDraft({ placements: updated });
	};

	const handleNext = () => {
		if (!draft.startDate) {
			setError("Start date is required.");
			return;
		}
		const geo = draft.geoTargeting;
		const hasPins = (geo?.locationPins?.length ?? 0) > 0;
		const hasCountries =
			(geo?.countries?.length ?? 0) > 0 ||
			(draft.targetCountries?.length ?? 0) > 0;
		if (!hasPins && !hasCountries) {
			setError("At least one location or target country is required.");
			return;
		}
		if (!draft.campaignBudgetOptimization && draft.budgetAmount <= 0) {
			setError("Budget amount must be greater than 0.");
			return;
		}
		if (
			(isLeads || isSales) &&
			draft.conversionLocation === MetaAdsConversionLocation.WEBSITE &&
			!draft.pixelId.trim()
		) {
			setError("Meta Pixel ID is required for Website conversion location.");
			return;
		}
		if (
			isSales &&
			draft.conversionLocation === MetaAdsConversionLocation.CALLS &&
			!draft.phoneNumber?.trim()
		) {
			setError("Phone number is required for Call conversions.");
			return;
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Ad Set Settings</Card.Header>
			<Card.Body>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{isLeads && (
						<>
							<h6>Conversion Location</h6>
							<div>
								{[
									{
										value: MetaAdsConversionLocation.INSTANT_FORM,
										label: "Instant Forms",
									},
									{
										value: MetaAdsConversionLocation.WEBSITE,
										label: "Website",
									},
									{
										value: MetaAdsConversionLocation.MESSENGER,
										label: "Messenger",
									},
								].map((opt) => (
									<div className="form-check" key={opt.value}>
										<input
											className="form-check-input"
											type="radio"
											id={`conv-loc-${opt.value}`}
											checked={draft.conversionLocation === opt.value}
											onChange={() =>
												updateDraft({ conversionLocation: opt.value })
											}
										/>
										<label
											className="form-check-label"
											htmlFor={`conv-loc-${opt.value}`}
										>
											{opt.label}
										</label>
									</div>
								))}
							</div>

							{draft.conversionLocation ===
								MetaAdsConversionLocation.WEBSITE && (
								<div>
									<label className="form-label" htmlFor="pixel-id">
										Meta Pixel
									</label>
									<PixelSelectField
										id="pixel-id"
										value={draft.pixelId}
										onChange={(pixelId) => updateDraft({ pixelId })}
									/>
								</div>
							)}
						</>
					)}

					{isReachOptimization && (
						<>
							<h6>Frequency Cap</h6>
							<div style={{ display: "flex", gap: 16 }}>
								<div style={{ flex: 1 }}>
									<label className="form-label" htmlFor="freq-cap-impressions">
										Max impressions per person
									</label>
									<input
										id="freq-cap-impressions"
										type="number"
										className="form-control"
										min={1}
										max={20}
										value={draft.frequencyCapImpressions ?? 2}
										onChange={(e) =>
											updateDraft({
												frequencyCapImpressions: Number(e.target.value),
											})
										}
									/>
								</div>
								<div style={{ flex: 1 }}>
									<label className="form-label" htmlFor="freq-cap-days">
										Per how many days
									</label>
									<input
										id="freq-cap-days"
										type="number"
										className="form-control"
										min={1}
										max={30}
										value={draft.frequencyCapIntervalDays ?? 7}
										onChange={(e) =>
											updateDraft({
												frequencyCapIntervalDays: Number(e.target.value),
											})
										}
									/>
								</div>
							</div>
						</>
					)}

					{isEngagement && (
						<>
							<h6>Conversion Location</h6>
							<div>
								{[
									{
										value: MetaAdsConversionLocation.ON_POST,
										label: "On Your Ad (Post Engagement)",
									},
									{
										value: MetaAdsConversionLocation.MESSENGER,
										label: "Messaging (Messenger)",
									},
									{
										value: MetaAdsConversionLocation.INSTAGRAM_DIRECT,
										label: "Messaging (Instagram Direct)",
									},
									{
										value: MetaAdsConversionLocation.ON_PAGE,
										label: "On Your Page (Page Likes)",
									},
								].map((opt) => (
									<div className="form-check" key={opt.value}>
										<input
											className="form-check-input"
											type="radio"
											id={`conv-loc-${opt.value}`}
											checked={draft.conversionLocation === opt.value}
											onChange={() => {
												let optGoal = MetaAdsOptimizationGoal.POST_ENGAGEMENT;
												if (
													opt.value === MetaAdsConversionLocation.MESSENGER ||
													opt.value ===
														MetaAdsConversionLocation.INSTAGRAM_DIRECT
												) {
													optGoal = MetaAdsOptimizationGoal.CONVERSATIONS;
												} else if (
													opt.value === MetaAdsConversionLocation.ON_PAGE
												) {
													optGoal = MetaAdsOptimizationGoal.PAGE_LIKES;
												}
												updateDraft({
													conversionLocation: opt.value,
													optimizationGoal: optGoal,
												});
											}}
										/>
										<label
											className="form-check-label"
											htmlFor={`conv-loc-${opt.value}`}
										>
											{opt.label}
										</label>
									</div>
								))}
							</div>

							{(draft.conversionLocation ===
								MetaAdsConversionLocation.MESSENGER ||
								draft.conversionLocation ===
									MetaAdsConversionLocation.INSTAGRAM_DIRECT) && (
								<div>
									<label className="form-label" htmlFor="messaging-greeting">
										Automated Greeting Message
									</label>
									<textarea
										id="messaging-greeting"
										className="form-control"
										rows={2}
										value={draft.messagingGreeting ?? ""}
										onChange={(e) =>
											updateDraft({ messagingGreeting: e.target.value })
										}
										placeholder="Hi! Thanks for reaching out. How can we help you today?"
									/>
									<small className="text-muted">
										This message is sent automatically when someone starts a
										conversation.
									</small>
								</div>
							)}
						</>
					)}

					{isSales && (
						<>
							<h6>Conversion Location</h6>
							<div>
								{[
									{
										value: MetaAdsConversionLocation.WEBSITE,
										label: "Website",
									},
									{
										value: MetaAdsConversionLocation.MESSENGER,
										label: "Messenger",
									},
									{
										value: MetaAdsConversionLocation.CALLS,
										label: "Phone Calls",
									},
								].map((opt) => (
									<div className="form-check" key={opt.value}>
										<input
											className="form-check-input"
											type="radio"
											id={`conv-loc-${opt.value}`}
											checked={draft.conversionLocation === opt.value}
											onChange={() =>
												updateDraft({ conversionLocation: opt.value })
											}
										/>
										<label
											className="form-check-label"
											htmlFor={`conv-loc-${opt.value}`}
										>
											{opt.label}
										</label>
									</div>
								))}
							</div>

							{draft.conversionLocation ===
								MetaAdsConversionLocation.WEBSITE && (
								<>
									<div>
										<label className="form-label" htmlFor="sales-pixel-id">
											Meta Pixel
										</label>
										<PixelSelectField
											id="sales-pixel-id"
											value={draft.pixelId}
											onChange={(pixelId) => updateDraft({ pixelId })}
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="conversion-event">
											Conversion Event
										</label>
										<Select
											id="conversion-event"
											className="form-select"
											value={
												draft.conversionEvent ?? MetaAdsConversionEvent.PURCHASE
											}
											onChange={(e) =>
												updateDraft({
													conversionEvent: e.target
														.value as MetaAdsConversionEvent,
												})
											}
										>
											<option value={MetaAdsConversionEvent.PURCHASE}>
												Purchase
											</option>
											<option value={MetaAdsConversionEvent.ADD_TO_CART}>
												Add to Cart
											</option>
											<option value={MetaAdsConversionEvent.INITIATE_CHECKOUT}>
												Initiate Checkout
											</option>
										</Select>
									</div>
								</>
							)}

							{draft.conversionLocation === MetaAdsConversionLocation.CALLS && (
								<div>
									<label className="form-label" htmlFor="phone-number">
										Phone Number
									</label>
									<input
										id="phone-number"
										type="tel"
										className="form-control"
										value={draft.phoneNumber ?? ""}
										onChange={(e) =>
											updateDraft({ phoneNumber: e.target.value })
										}
										placeholder="+1 (555) 123-4567"
									/>
								</div>
							)}

							<div className="form-check form-switch">
								<input
									className="form-check-input"
									type="checkbox"
									id="dco-toggle"
									checked={draft.dynamicCreativeOptimization ?? false}
									onChange={(e) =>
										updateDraft({
											dynamicCreativeOptimization: e.target.checked,
										})
									}
								/>
								<label className="form-check-label" htmlFor="dco-toggle">
									Dynamic Creative Optimization
								</label>
								<small className="text-muted d-block">
									Let Meta automatically test different combinations of your
									creative elements.
								</small>
							</div>

							<h6>Attribution Window</h6>
							<div>
								{[
									{
										value: MetaAdsAttributionWindow.ONE_DAY_CLICK,
										label: "1-day click",
									},
									{
										value: MetaAdsAttributionWindow.SEVEN_DAY_CLICK,
										label: "7-day click",
									},
									{
										value: MetaAdsAttributionWindow.ONE_DAY_VIEW,
										label: "1-day view",
									},
								].map((opt) => (
									<div className="form-check" key={opt.value}>
										<input
											className="form-check-input"
											type="checkbox"
											id={`attr-${opt.value}`}
											checked={(draft.attributionWindows ?? []).includes(
												opt.value,
											)}
											onChange={(e) => {
												const current = draft.attributionWindows ?? [];
												const updated = e.target.checked
													? [...current, opt.value]
													: current.filter((w) => w !== opt.value);
												updateDraft({ attributionWindows: updated });
											}}
										/>
										<label
											className="form-check-label"
											htmlFor={`attr-${opt.value}`}
										>
											{opt.label}
										</label>
									</div>
								))}
							</div>
						</>
					)}

					{!draft.campaignBudgetOptimization && (
						<>
							<h6>Budget</h6>
							<div>
								<label className="form-label" htmlFor="adset-budget-type">
									Budget Type
								</label>
								<Select
									id="adset-budget-type"
									className="form-select"
									value={draft.budgetType}
									onChange={(e) =>
										updateDraft({
											budgetType: e.target.value as MetaAdsBudgetType,
										})
									}
								>
									<option value={MetaAdsBudgetType.DAILY}>Daily Budget</option>
									<option value={MetaAdsBudgetType.LIFETIME}>
										Lifetime Budget
									</option>
								</Select>
							</div>
							<div>
								<label className="form-label" htmlFor="adset-budget">
									Budget Amount
								</label>
								<div className="input-group">
									<span className="input-group-text">$</span>
									<input
										id="adset-budget"
										type="number"
										className="form-control"
										value={centsToDollars(draft.budgetAmount)}
										onChange={(e) =>
											updateDraft({
												budgetAmount: dollarsToCents(Number(e.target.value)),
											})
										}
										min={0}
										step={0.01}
									/>
								</div>
							</div>
						</>
					)}

					<h6>Schedule</h6>
					<div style={{ display: "flex", gap: 16 }}>
						<div style={{ flex: 1 }}>
							<label className="form-label" htmlFor="start-date">
								Start Date
							</label>
							<input
								id="start-date"
								type="date"
								className="form-control"
								value={draft.startDate}
								onChange={(e) => updateDraft({ startDate: e.target.value })}
							/>
						</div>
						<div style={{ flex: 1 }}>
							<label className="form-label" htmlFor="end-date">
								End Date (optional)
							</label>
							<input
								id="end-date"
								type="date"
								className="form-control"
								value={draft.endDate ?? ""}
								onChange={(e) =>
									updateDraft({
										endDate: e.target.value || null,
									})
								}
							/>
						</div>
					</div>

					<h6>Placements</h6>
					<div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								id="placement-auto"
								checked={placementMode === "automatic"}
								onChange={() => {
									setPlacementMode("automatic");
									updateDraft({ placements: "automatic" });
								}}
							/>
							<label className="form-check-label" htmlFor="placement-auto">
								Automatic Placements (Recommended)
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								id="placement-manual"
								checked={placementMode === "manual"}
								onChange={() => {
									setPlacementMode("manual");
									updateDraft({ placements: [] });
								}}
							/>
							<label className="form-check-label" htmlFor="placement-manual">
								Manual Placements
							</label>
						</div>
					</div>

					{placementMode === "manual" && (
						<div style={{ paddingLeft: 16 }}>
							{PLACEMENT_OPTIONS.map((opt) => (
								<div className="form-check" key={opt.id}>
									<input
										className="form-check-input"
										type="checkbox"
										id={`placement-${opt.id}`}
										checked={manualPlacements.includes(opt.id)}
										onChange={() => handlePlacementToggle(opt.id)}
									/>
									<label
										className="form-check-label"
										htmlFor={`placement-${opt.id}`}
									>
										{opt.label}
									</label>
								</div>
							))}
						</div>
					)}

					<ErrorBoundary
						fallback={
							<div className="alert alert-warning" style={{ fontSize: 14 }}>
								Geo-targeting map could not be loaded. You can still set
								countries and continue.
							</div>
						}
					>
						<GeoTargetingSection />
					</ErrorBoundary>

					<AudienceTargetingSection />

					<InterestTargetingSection />

					<h6>Demographics</h6>
					<div style={{ display: "flex", gap: 16 }}>
						<div style={{ flex: 1 }}>
							<label className="form-label" htmlFor="age-min">
								Min Age
							</label>
							<input
								id="age-min"
								type="number"
								className="form-control"
								value={draft.ageMin}
								onChange={(e) =>
									updateDraft({ ageMin: Number(e.target.value) })
								}
								min={18}
								max={65}
							/>
						</div>
						<div style={{ flex: 1 }}>
							<label className="form-label" htmlFor="age-max">
								Max Age
							</label>
							<input
								id="age-max"
								type="number"
								className="form-control"
								value={draft.ageMax}
								onChange={(e) =>
									updateDraft({ ageMax: Number(e.target.value) })
								}
								min={18}
								max={65}
							/>
						</div>
					</div>

					<div>
						<label className="form-label" htmlFor="adset-gender">
							Gender
						</label>
						<Select
							id="adset-gender"
							className="form-select"
							value={draft.genders?.[0] ?? 0}
							onChange={(e) =>
								updateDraft({ genders: [Number(e.target.value)] })
							}
						>
							<option value={0}>All</option>
							<option value={1}>Male</option>
							<option value={2}>Female</option>
						</Select>
					</div>

					<h6>Optimization</h6>
					<div>
						<label className="form-label" htmlFor="adset-optimization">
							Optimization Goal
						</label>
						<Select
							id="adset-optimization"
							className="form-select"
							value={draft.optimizationGoal}
							onChange={(e) => {
								const optimizationGoal = e.target
									.value as MetaAdsOptimizationGoal;
								if (
									draft.objective ===
										MetaAdsCampaignObjective.OUTCOME_AWARENESS &&
									optimizationGoal === MetaAdsOptimizationGoal.REACH
								) {
									updateDraft({
										optimizationGoal,
										frequencyCapImpressions: draft.frequencyCapImpressions ?? 2,
										frequencyCapIntervalDays:
											draft.frequencyCapIntervalDays ?? 7,
									});
									return;
								}
								if (
									draft.objective === MetaAdsCampaignObjective.OUTCOME_AWARENESS
								) {
									updateDraft({
										optimizationGoal,
										frequencyCapImpressions: undefined,
										frequencyCapIntervalDays: undefined,
									});
									return;
								}
								updateDraft({ optimizationGoal });
							}}
						>
							{getOptimizationGoalsForObjective(draft.objective).map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</div>

					<div>
						<label className="form-label" htmlFor="adset-bid-strategy">
							Bid Strategy
						</label>
						<Select
							id="adset-bid-strategy"
							className="form-select"
							value={draft.bidStrategy}
							onChange={(e) =>
								updateDraft({
									bidStrategy: e.target.value as MetaAdsBidStrategy,
								})
							}
						>
							<option value={MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP}>
								Lowest Cost
							</option>
							<option value={MetaAdsBidStrategy.LOWEST_COST_WITH_BID_CAP}>
								Bid Cap
							</option>
							<option value={MetaAdsBidStrategy.COST_CAP}>Cost Cap</option>
						</Select>
					</div>

					{draft.bidStrategy !== MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP && (
						<div>
							<label className="form-label" htmlFor="bid-amount">
								Bid/Cost Cap Amount
							</label>
							<div className="input-group">
								<span className="input-group-text">$</span>
								<input
									id="bid-amount"
									type="number"
									className="form-control"
									value={
										draft.bidAmount != null
											? centsToDollars(draft.bidAmount)
											: ""
									}
									onChange={(e) =>
										updateDraft({
											bidAmount: e.target.value
												? dollarsToCents(Number(e.target.value))
												: null,
										})
									}
									min={0}
									step={0.01}
								/>
							</div>
						</div>
					)}

					{error && <div className="text-danger">{error}</div>}

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<button
							type="button"
							className="btn btn-outline-secondary"
							onClick={prevStep}
						>
							Back
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleNext}
						>
							Next
						</button>
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
